import React from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import cogoToast from 'cogo-toast';
import { useTranslation } from 'react-i18next';

// Services
import { searchAddressREST } from '../../services/apiRouterService';
import { formatErrorMessage } from '../../services/errorMessageService';
import { AddressResp } from '../../../../../../types/Common';
import { ReturnOptionProps } from '../../../../../../types/ProductFactory';
import { canFetch } from '../../../../../tarifications/src/legacy/ProFlowAddressComponentV2';

// Quick search components
import AddressSearchQuickBE from './AddressSearchQuickBE';
import AddressSearchQuickNL from './AddressSearchQuickNL';

interface Props {
  onSelected: (arg0: ReturnOptionProps) => void;
  error?: string;
  setIsManualActive?: (arg0: boolean) => void;
  countryCode?: string;
}

export interface AddressOption {
  value: {
    street?: string | null;
    zipcode?: number | string | null;
    housenr?: string | null;
    country_code?: string | null;
    boxnr?: number | string | null;
    city?: string | null;
    self?: string;
  };
  label: string;
}

export const hasNumberAfterStreet = (address: string): boolean =>
  !!address.match(/[A-Za-z]+\s+\d+/);

const AddressSearchSelectorV2: React.FC<React.PropsWithChildren<Props>> = ({
  onSelected,
  error,
  setIsManualActive = () => null,
  children,
  countryCode,
  ...otherProps
}) => {
  const { t } = useTranslation();

  const fetchAddresses = async (
    address: string,
  ): Promise<AddressResp | undefined> => {
    const [resp, status] = await searchAddressREST(
      address,
      undefined,
      countryCode,
    );

    if (status !== 200) {
      if (countryCode === 'NL') {
        cogoToast.error(
          t('COMMON_COMPONENTS_ADDRESS-SEARCH-SELECTOR-V2_NL-ERROR'),
        );
      } else {
        const { detail } = formatErrorMessage('', resp?.detail);
        cogoToast.error(detail);
      }
      return;
    }

    return resp;
  };

  const searchAddresses = async (
    address: string,
    cb: (arg0: AddressOption[]) => void,
  ) => {
    if (!canFetch(address)) return cb([]);

    const addresses = await fetchAddresses(address);
    if (!addresses) return cb([]);

    cb(
      addresses?.items?.map(
        ({
          address: {
            city,
            boxnr,
            country_code,
            housenr,
            street,
            zipcode,
            ...rest
          },
          self,
        }) => ({
          value: {
            street,
            boxnr,
            housenr,
            zipcode,
            city,
            country_code,
            self,
            ...rest,
          },
          label: `${street} ${housenr}${
            boxnr ? `/${boxnr}` : ``
          }, ${zipcode} ${city} `,
        }),
      ),
    );
  };

  const handleOnCantFind = () => {
    setIsManualActive(true);
    onSelected({ name: 'address', value: {}, label: 'address' });
  };

  const [debounceSearchAddresses] = useDebouncedCallback(searchAddresses, 500);

  const quickSearchComponent = (() => {
    switch (countryCode) {
      case 'NL':
        return (
          <AddressSearchQuickNL
            handleOnCantFind={handleOnCantFind}
            loadOptions={debounceSearchAddresses}
            onSelected={onSelected}
          />
        );
      default:
        return (
          <AddressSearchQuickBE
            handleOnCantFind={handleOnCantFind}
            loadOptions={debounceSearchAddresses}
            onSelected={onSelected}
            error={error}
          />
        );
    }
  })();

  return (
    <>
      <Label className="title">{children}</Label>
      {quickSearchComponent}
    </>
  );
};

const Label = styled.label`
  color: #8990a3;
  font-weight: 400;
  font-size: 1.4rem;
`;

export default AddressSearchSelectorV2;
